import api from './base-api';
import {
	CreateMatchExclusionDto,
	GetMoreMatchesQueryDto,
	PaginatedMoreMatchesResponseDto,
	latestMatchInfoDto,
	MatchExclusionDto,
	MatchExclusionWithDisplayDto,
	RoundGroupDto,
	UpdateMatchStatusDto,
	UpdateMatchStatusResponse,
} from '@sportspredict/shared';

export const matchesApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getMoreMatchesByEvent: builder.query<PaginatedMoreMatchesResponseDto, GetMoreMatchesQueryDto>({
			query: ({ excludeMatchIds, ...params }) => ({
				url: 'matches/event/more-matches',
				params: {
					...params,
					excludeMatchIds: excludeMatchIds?.join(','),
				},
			}),
			providesTags: (result, error, { eventId, lobbyId, tab }) => [
				{
					type: 'Game',
					id: `${eventId}-${lobbyId ?? 'all'}-more-matches-${tab ?? 'auto'}`,
				},
			],
			extraOptions: { skipAuth: true },
		}),
		getLatestMatchInfo: builder.query<latestMatchInfoDto, { eventId: string }>({
			query: ({ eventId }) => ({
				url: `matches/latest-info/${eventId}`,
			}),
			extraOptions: { skipAuth: true },
		}),
		getLatestMatchIdByEventId: builder.query<{ id: string } | null, { eventId: string }>({
			query: ({ eventId }) => ({
				url: `matches/latest/${eventId}`,
			}),
			extraOptions: { skipAuth: true },
		}),
		refreshMatchResults: builder.mutation<{ success: boolean }, { match_id: string }>({
			query: (data) => ({
				url: 'matches/refresh-results',
				method: 'POST',
				body: data,
			}),
		}),
		getRoundGroups: builder.query<RoundGroupDto[], string>({
			query: (eventId) => ({
				url: `matches/round-groups/${eventId}`,
				method: 'GET',
			}),
		}),
		getMatchExclusions: builder.query<MatchExclusionWithDisplayDto[], string>({
			query: (eventId) => ({
				url: `matches/exclusions/${eventId}`,
				method: 'GET',
			}),
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }) => ({ type: 'MatchExclusion' as const, id })),
							{ type: 'MatchExclusion' as const, id: 'LIST' },
						]
					: [{ type: 'MatchExclusion' as const, id: 'LIST' }],
		}),
		addMatchExclusion: builder.mutation<void, CreateMatchExclusionDto>({
			query: (body) => ({
				url: 'matches/exclusions',
				method: 'POST',
				body,
			}),
			invalidatesTags: [{ type: 'MatchExclusion', id: 'LIST' }],
		}),
		removeMatchExclusion: builder.mutation<void, string>({
			query: (id) => ({
				url: `matches/exclusions/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [{ type: 'MatchExclusion', id: 'LIST' }],
		}),
		bulkDeleteMatches: builder.mutation<void, { matchIds: string[] }>({
			query: (body) => ({
				url: 'matches/bulk-delete',
				method: 'POST',
				body,
			}),
		}),
		bulkAddMatchExclusions: builder.mutation<
			MatchExclusionDto[],
			{ exclusions: CreateMatchExclusionDto[] }
		>({
			query: (body) => ({
				url: 'matches/exclusions/bulk',
				method: 'POST',
				body,
			}),
			invalidatesTags: [{ type: 'MatchExclusion', id: 'LIST' }],
		}),
		updateMatchStatus: builder.mutation<UpdateMatchStatusResponse, UpdateMatchStatusDto>({
			query: (body) => ({
				url: 'matches/status',
				method: 'POST',
				body,
			}),
		}),
	}),
});

export const {
	useGetMoreMatchesByEventQuery,
	useLazyGetMoreMatchesByEventQuery,
	useGetLatestMatchInfoQuery,
	useGetLatestMatchIdByEventIdQuery,
	useRefreshMatchResultsMutation,
	useGetRoundGroupsQuery,
	useGetMatchExclusionsQuery,
	useAddMatchExclusionMutation,
	useRemoveMatchExclusionMutation,
	useBulkDeleteMatchesMutation,
	useBulkAddMatchExclusionsMutation,
	useUpdateMatchStatusMutation,
} = matchesApi;
